import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useScroll, useInView } from "framer-motion"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useViewport } from "contexts/Viewport"

import { fullGrid } from "css/grid"
import getMediaQuery from "css/breakpoints"
import { spacing } from "css/spacing"

import { useRoller, RollerProvider } from "components/roller/context"
import Card from "components/watch-suggestions/SuggestionCard"
import Arrows from "components/watch-suggestions/Arrows"

const Root = styled.section`
  ${fullGrid}
  ${spacing}
  align-items: center;
  overflow: hidden;
`

const List = styled.ul`
  display: grid;
  grid-auto-flow: column;
  grid-column: doc;
  grid-row: roller;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  --col-nb: 6;
  grid-auto-columns: calc(var(--grid-col-unit) * var(--col-nb) + var(--grid-gap) * (var(--col-nb) - 1));
  ${getMediaQuery("m")} {
    grid-auto-columns: unset;
  }

  ::-webkit-scrollbar {
    display: none;
  }

  > li {
    scroll-snap-align: start;
  }
`

const Strip = forwardRef(function Strip({ className, items }, ref) {
  const console = useConsole()

  const [showArrows, setShowArrows] = useState(true)

  const { width } = useViewport()

  const { unit, max, isTouchDevice } = useRoller()
  const { scrollX } = useScroll({ container: ref })

  function updateUnit() {
    const u = [...ref.current.childNodes][0]?.clientWidth
    unit.set(u)
    max.set(ref.current.scrollWidth - ref.current.clientWidth - unit.get() / 2)
    setShowArrows(max.current > 0)
  }

  const onWchange = () => updateUnit()
  useEffect(() => width.onChange(onWchange))

  useLayoutEffect(() => {
    updateUnit()
  }, [])

  return (
    <>
      <List ref={ref} className={className}>
        {items.map((item, index) => (
          <Card key={index} {...item} />
        ))}
      </List>
      {showArrows && !isTouchDevice ? <Arrows scrollX={scrollX} ref={ref} /> : null}
    </>
  )
})

export default function WatchSuggestions({ className, items }) {
  const console = useConsole()
  const ref = useRef()
  const isInView = useInView(ref, { once: true })

  console.verbose("WatchSuggestions(%o)", items)
  return (
    <RollerProvider>
      <Root className={`${className}${isInView ? " show" : ""}`}>
        <Strip ref={ref} items={items} />
      </Root>
    </RollerProvider>
  )
}
